// src/pages/Login.jsx
import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  LogIn,
  Mail,
  Lock,
  AlertCircle,
  ArrowRight,
  ShieldCheck,
  Loader2,
  Eye,
  EyeOff,
  Recycle,
} from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']); // 6 digits
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [authMode, setAuthMode] = useState('password'); // 'password' | 'otp'
  const [otpSent, setOtpSent] = useState(false);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);

  const { login, api, isAuthenticated, user } = useAuth(); // ← added isAuthenticated, user for redirection
  const navigate = useNavigate();

  // Refs for OTP inputs (auto-focus)
  const otpRefs = useRef([]);

  // Focus first OTP input when OTP mode is active and OTP is sent
  useEffect(() => {
    if (authMode === 'otp' && otpSent && otpRefs.current[0]) {
      otpRefs.current[0].focus();
    }
  }, [authMode, otpSent]);

  // ← NEW: Navigate only AFTER isAuthenticated becomes true
  // Determine appropriate landing path based on role
  useEffect(() => {
    if (isAuthenticated && api) {
      const dest = (user) => {
        if (user?.role === 'ngo') return '/ngo-dashboard';
        if (user?.role === 'volunteer') return '/user-dashboard';
        if (user?.role === 'admin') return '/admin-dashboard';
        return '/dashboard';
      };
      // we can access user via api.defaults header or call profile if needed,
      // but once isAuthenticated true the context will have user set.
      // useAuth returns user as well so we can destructure above.
      const redirectPath = dest(user);
      if (redirectPath) navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, navigate, api]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsEmailVerification(false);

    const cleanEmail = email.trim();
    const cleanPass = password.trim();

    if (!cleanEmail) {
      setError('Email is required');
      toast.error('Please enter your email');
      return;
    }

    if (!cleanPass) {
      setError('Password is required');
      toast.error('Please enter your password');
      return;
    }

    setIsLoading(true);

    try {
      await login(cleanEmail, cleanPass);
      // NO navigate here anymore → useEffect will handle it when isAuthenticated flips
    } catch (err) {
      let msg = 'Invalid email or password. Please try again.';
      const errCode = err.response?.data?.code;

      if (err.response?.status === 401) {
        msg = 'Incorrect email or password.';
      } else if (err.response?.data?.message) {
        msg = err.response.data.message;

        if (errCode === 'EMAIL_NOT_VERIFIED') {
          setNeedsEmailVerification(true);
        }

        if (msg.includes('disposable') || msg.includes('temporary')) {
          msg = 'Temporary/disposable emails are not allowed.';
        } else if (msg.includes('invalid') || msg.includes('domain')) {
          msg = 'Invalid email domain.';
        } else if (msg.includes('no password set')) {
          msg = 'No password set for this account. Try Email OTP login.';
          setAuthMode('otp');
        }
      }

      setError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const cleanEmail = email.trim();
    if (!cleanEmail) {
      toast.error('Please enter your email first.');
      return;
    }

    setIsLoading(true);
    try {
      const res = await api.post('/api/auth/resend-verification', { email: cleanEmail });
      toast.success(res.data?.message || 'Verification link sent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resend verification link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async () => {
    const cleanEmail = email.trim();
    if (!cleanEmail) {
      setError('Email is required');
      toast.error('Email is required');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      await api.post('/api/auth/send-otp', { email: cleanEmail });
      setOtpSent(true);
      setOtp(['', '', '', '', '', '']); // reset OTP fields
      toast.success('OTP sent! Check your inbox/spam folder.');
      setError('');
    } catch (err) {
      let msg = err.response?.data?.message || 'Failed to send OTP.';

      if (msg.includes('disposable') || msg.includes('temporary')) {
        msg = 'Temporary/disposable emails are not allowed.';
      } else if (msg.includes('invalid') || msg.includes('domain')) {
        msg = 'Invalid email domain.';
      } else if (msg.includes('already registered')) {
        msg = 'Email already registered. Try password login.';
      }

      setError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const { verifyOtp } = useAuth();
  const handleVerifyOtp = async () => {
    const cleanOtp = otp.join('');
    if (cleanOtp.length !== 6 || !/^\d{6}$/.test(cleanOtp)) {
      setError('Please enter a valid 6-digit OTP');
      toast.error('Invalid OTP format');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      await verifyOtp(email.trim(), cleanOtp);
      // Success toast and navigation handled by AuthContext/useEffect
    } catch (err) {
      setError(err.message || 'Invalid or expired OTP. Please try again.');
      toast.error(err.message || 'Invalid or expired OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (value, index) => {
    if (value.length > 1) value = value[0]; // only one digit
    if (!/^\d?$/.test(value)) return; // only digits or empty

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e, index) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').trim();

    if (/^\d{6}$/.test(pasted)) {
      const digits = pasted.split('');
      setOtp(digits);

      // Focus last input or submit if complete
      if (otpRefs.current[5]) otpRefs.current[5].focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const highlights = [
    'Access role-based dashboards for volunteers, NGOs, agents, and admins.',
    'Track opportunities, messages, notifications, and pickups in one place.',
    'Use password login or secure email OTP based on your preference.',
  ];

  const inputWithIconClass = 'block w-full rounded-2xl border border-slate-300 bg-white pl-11 pr-4 py-3 text-slate-900 shadow-sm transition-all duration-200 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-100';
  const primaryButtonClass = 'w-full flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-6 py-3.5 text-white font-semibold shadow-sm transition-all duration-200 hover:bg-emerald-700 hover:shadow-lg hover:shadow-emerald-200/60 disabled:cursor-not-allowed disabled:opacity-60 active:scale-[0.98]';
  const otpBoxClass = 'h-12 w-12 rounded-xl border border-slate-300 bg-white text-center text-2xl font-mono text-slate-900 shadow-sm transition-all duration-200 focus:border-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-100';

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 px-4 py-10 sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(16,185,129,0.16),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(20,184,166,0.12),transparent_24%)]" />
      <div className="relative mx-auto flex min-h-[calc(100vh-5rem)] max-w-5xl items-center">
        <div className="grid w-full items-start overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-[0_30px_80px_rgba(15,23,42,0.45)] lg:grid-cols-[0.92fr_1.08fr]">
          <aside className="relative hidden lg:flex h-full flex-col justify-start gap-10 overflow-hidden bg-slate-900 px-8 py-9 text-white xl:gap-12">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.22),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(20,184,166,0.18),transparent_28%)]" />
            <div className="relative z-10">
              <div className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-slate-200 backdrop-blur">
                <Recycle className="h-4 w-4 text-emerald-400" />
                WasteZero Access Portal
              </div>
              <h1 className="mt-8 text-4xl font-bold leading-tight tracking-tight">
                Welcome back to your cleaner city workspace.
              </h1>
              <p className="mt-4 max-w-md text-base leading-7 text-slate-300">
                Sign in to continue managing opportunities, applications, pickups, and communication from one professional dashboard.
              </p>
            </div>

            <div className="relative z-10 space-y-4">
              {highlights.map((item) => (
                <div key={item} className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300">
                      <ShieldCheck className="h-4 w-4" />
                    </div>
                    <p className="text-sm leading-6 text-slate-200">{item}</p>
                  </div>
                </div>
              ))}
            </div>
          </aside>

          <div className="flex flex-col justify-start px-6 py-8 sm:px-8 sm:py-10 lg:px-12">
            <div className="mb-8 flex items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
                  Secure Sign In
                </div>
                <h2 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Welcome back</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Use your password or email OTP to access your WasteZero account.
                </p>
              </div>
              <div className="hidden h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 sm:flex">
                <LogIn className="h-6 w-6" />
              </div>
            </div>

            {error && (
              <div className="mb-6 flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {authMode === 'password' && needsEmailVerification && (
              <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
                <p className="text-sm">Your account is not verified yet. Please verify email first.</p>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={isLoading}
                  className="mt-2 text-sm font-semibold text-amber-700 hover:text-amber-900 hover:underline disabled:opacity-60"
                >
                  Resend verification link
                </button>
              </div>
            )}

            <div className="mb-8 grid grid-cols-2 gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-1.5">
              <button
                type="button"
                onClick={() => {
                  setAuthMode('password');
                  setError('');
                  setOtpSent(false);
                  setOtp(['', '', '', '', '', '']);
                }}
                className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  authMode === 'password'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Password
              </button>
              <button
                type="button"
                onClick={() => {
                  setAuthMode('otp');
                  setError('');
                  setOtpSent(false);
                  setOtp(['', '', '', '', '', '']);
                }}
                className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  authMode === 'otp'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Email OTP
              </button>
            </div>

            {authMode === 'password' ? (
              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Email address
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                      <Mail className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputWithIconClass}
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Password
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                      <Lock className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`${inputWithIconClass} pr-12`}
                      placeholder="••••••••"
                      required
                      autoComplete="current-password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className={primaryButtonClass}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            ) : (
              <div className="space-y-6">
                <div>
                  <label htmlFor="email-otp" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Email address
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                      <Mail className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      id="email-otp"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputWithIconClass}
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                      disabled={isLoading || otpSent}
                    />
                  </div>
                </div>

                {!otpSent ? (
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={isLoading || !email.trim()}
                    className={primaryButtonClass}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send OTP to Email'
                    )}
                  </button>
                ) : (
                  <>
                    <div>
                      <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-slate-700">
                        <ShieldCheck className="h-5 w-5 text-emerald-600" />
                        Enter 6-digit OTP
                      </label>
                      <div className="flex justify-center gap-3">
                        {otp.map((digit, index) => (
                          <input
                            key={index}
                            id={`otp-${index}`}
                            ref={(el) => (otpRefs.current[index] = el)}
                            type="text"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleOtpChange(e.target.value, index)}
                            onPaste={(e) => handleOtpPaste(e, index)}
                            onKeyDown={(e) => handleOtpKeyDown(e, index)}
                            className={otpBoxClass}
                            autoComplete="one-time-code"
                            disabled={isLoading}
                          />
                        ))}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleVerifyOtp}
                      disabled={isLoading || otp.some(d => !d)}
                      className={primaryButtonClass}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        'Verify & Login'
                      )}
                    </button>

                    <div className="text-center mt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setOtpSent(false);
                          setOtp(['', '', '', '', '', '']);
                        }}
                        className="text-sm font-medium text-emerald-700 transition-colors hover:text-emerald-800 hover:underline disabled:opacity-50"
                        disabled={isLoading}
                      >
                        Didn't receive OTP? Resend
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="mt-8 space-y-4 text-center">
              <p className="text-sm text-slate-600">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="font-semibold text-emerald-700 transition-colors hover:text-emerald-800 hover:underline"
                >
                  Create one now
                </Link>
              </p>

              <Link
                to="/forgot-password"
                className="text-sm font-medium text-slate-500 transition-colors hover:text-emerald-700 hover:underline"
              >
                Forgot your password?
              </Link>
            </div>

            <p className="mt-8 text-center text-xs uppercase tracking-[0.18em] text-slate-400">
              Secure login • Your data stays private
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}